import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'contexts/LocationContext';
import { useKeyDown } from 'hooks';
import { ChevronRight, House, PiggyBank, SolarPanel } from 'svg';
import { Location, MapType } from 'types';
import {
  LoansPanel,
  LocationPanel,
  PopularProductsPanel,
  SolarInfoPanel
} from './components';
import { useSuburbList } from './useSuburbList';

export type FunFacts = {
  mostGwhGeneratedValue: number;
  mostGwhGeneratedLocation: Location | null;
  highestAverageSolarCapacityValue: number;
  highestAverageSolarCapacityLocation: Location | null;
  mostSolarInstallationsValue: number;
  mostSolarInstallationsLocation: Location | null;
  mostBatteryInstallationsValue: number;
  mostBatteryInstallationsLocation: Location | null;
  highestBatteryUptakeValue: number;
  highestBatteryUptakeLocation: Location | null;
  mostInsulationInstallationValue: number;
  mostInsulationInstallationLocation: Location | null;
  mostElectricVehiclePurchaseValue: number;
  mostElectricVehiclePurchaseLocation: Location | null;
  highestNumberOfLoansValue: number;
  highestNumberOfLoansLocation: Location | null;
  highestTotalLoanAmountValue: number;
  highestTotalLoanAmountLocation: Location | null;
  highestAvgLoanAmountValue: number;
  highestAvgLoanAmountLocation: Location | null;
};

type InfoPanelProps = {
  changeMapType: (type: MapType) => void;
  setHeatMapFilter: (filter: string) => void;
  locations: Location[];
};

export default function InfoPanel({
  changeMapType,
  setHeatMapFilter,
  locations
}: InfoPanelProps) {
  const [location, setLocation] = useLocation();

  const [isPopularProductsPanelExpanded, setIsPopularProductsPanelExpanded] =
    useState(false);
  const [isLoansPanelExpanded, setIsLoansPanelExpanded] = useState(false);
  const [isSolarInfoPanelExpanded, setIsSolarInfoPanelExpanded] =
    useState(false);

  const { searchInput, filteredItems, handleInputChange, clearSearch } =
    useSuburbList(locations);

  // detect user pressing 'Esc'
  useKeyDown(() => {
    closeAllPanel();
  }, 'Escape');

  useEffect(() => {
    if (location) {
      clearSearch();
      closeAllPanel();
    }
  }, [location, clearSearch]);

  const closeAllPanel = () => {
    setIsPopularProductsPanelExpanded(false);
    setIsLoansPanelExpanded(false);
    setIsSolarInfoPanelExpanded(false);
  };

  const funFacts = useMemo((): FunFacts => {
    const initialFunFacts: FunFacts = {
      mostGwhGeneratedValue: 0,
      mostGwhGeneratedLocation: null,
      highestAverageSolarCapacityValue: 0,
      highestAverageSolarCapacityLocation: null,
      mostSolarInstallationsValue: 0,
      mostSolarInstallationsLocation: null,
      mostBatteryInstallationsValue: 0,
      mostBatteryInstallationsLocation: null,
      highestBatteryUptakeValue: 0,
      highestBatteryUptakeLocation: null,
      mostInsulationInstallationValue: 0,
      mostInsulationInstallationLocation: null,
      mostElectricVehiclePurchaseValue: 0,
      mostElectricVehiclePurchaseLocation: null,
      highestNumberOfLoansValue: 0,
      highestNumberOfLoansLocation: null,
      highestTotalLoanAmountValue: 0,
      highestTotalLoanAmountLocation: null,
      highestAvgLoanAmountValue: 0,
      highestAvgLoanAmountLocation: null
    };

    return locations.reduce((funFacts, location) => {
      // battery uptake calculation
      const totalSolar = location.numSettledSolarSystem || 0;
      const totalSolarAndBattery =
        location.numSettledSolarAndBatterySystem || 0;
      const totalBattery = location.numSettledBatteryStorage || 0;

      const denominator = totalSolar + totalSolarAndBattery + totalBattery;

      const batteryUptake =
        denominator !== 0
          ? Math.round(
              (100 / denominator) * (totalBattery + totalSolarAndBattery)
            )
          : 0;

      // Solar Info Data
      if (
        location.avgTotalPanelsCapacityKilowatt &&
        location.avgTotalPanelsCapacityKilowatt >
          funFacts.highestAverageSolarCapacityValue
      ) {
        funFacts.highestAverageSolarCapacityValue =
          location.avgTotalPanelsCapacityKilowatt;
        funFacts.highestAverageSolarCapacityLocation = location;
      }

      // Popular Products Data
      if (
        location.numSettledAllSolar &&
        location.numSettledAllSolar > funFacts.mostSolarInstallationsValue
      ) {
        funFacts.mostSolarInstallationsValue = location.numSettledAllSolar;
        funFacts.mostSolarInstallationsLocation = location;
      }

      if (batteryUptake && batteryUptake > funFacts.highestBatteryUptakeValue) {
        funFacts.highestBatteryUptakeValue = batteryUptake;
        funFacts.highestBatteryUptakeLocation = location;
      }

      if (
        location.numSettledInsulation &&
        location.numSettledInsulation > funFacts.mostInsulationInstallationValue
      ) {
        funFacts.mostInsulationInstallationValue =
          location.numSettledInsulation;
        funFacts.mostInsulationInstallationLocation = location;
      }

      if (
        location.numSettledAllElectricVehicles &&
        location.numSettledAllElectricVehicles >
          funFacts.mostElectricVehiclePurchaseValue
      ) {
        funFacts.mostElectricVehiclePurchaseValue =
          location.numSettledAllElectricVehicles;
        funFacts.mostElectricVehiclePurchaseLocation = location;
      }

      // Loans Data
      if (
        location.numSettled &&
        location.numSettled > funFacts.highestNumberOfLoansValue
      ) {
        funFacts.highestNumberOfLoansValue = location.numSettled;
        funFacts.highestNumberOfLoansLocation = location;
      }

      if (
        location.amountSettled &&
        location.amountSettled > funFacts.highestTotalLoanAmountValue
      ) {
        funFacts.highestTotalLoanAmountValue = location.amountSettled;
        funFacts.highestTotalLoanAmountLocation = location;
      }

      if (
        location.avgAmountSettled &&
        location.avgAmountSettled > funFacts.highestAvgLoanAmountValue
      ) {
        funFacts.highestAvgLoanAmountValue = location.avgAmountSettled;
        funFacts.highestAvgLoanAmountLocation = location;
      }

      return funFacts;
    }, initialFunFacts);
  }, [locations]);

  return (
    <>
      <ul id="map-info-panel" className="info-panel">
        <li className="relative info-panel-box bg-black">
          <p className="info-panel-child-box-description text-lg">
            Find your area
          </p>
          <input
            type="text"
            id="suburb-postcode-search"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:ring-primary focus:border-primary block w-full p-2.5"
            placeholder="Suburb or postcode"
            value={searchInput}
            onChange={(e) => handleInputChange(e.target.value)}
            required
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls="suburb-postcode-list"
            aria-label="Search for a suburb or postcode"
            data-lpignore="true"
          />

          {searchInput && (
            <div className="absolute inset-y-0 flex items-center pr-3 top-7 right-3">
              <ChevronRight fill="black" width={20} height={20} />
            </div>
          )}

          <div className="absolute left-[20px] right-[20px] z-10 mt-2 bg-white rounded shadow-lg max-h-36 overflow-y-auto">
            <ul
              aria-label="List of locations"
              role="listbox"
              id="suburb-postcode-list"
            >
              {filteredItems.map((item, index) => (
                <li
                  key={item.location}
                  onClick={() => setLocation(item)}
                  role="option"
                  aria-selected="false"
                  id={`search-list-item-${index}`}
                  className="search-list-item py-2 px-4 cursor-pointer hover:bg-tertiary"
                >
                  {item.location} {item.postcode}
                </li>
              ))}
            </ul>
          </div>
        </li>
        <li>
          <a
            className="info-panel-box bg-primary hidden sm:block"
            aria-labelledby="popular-products"
            href="javascript:void(0);"
            aria-expanded={isPopularProductsPanelExpanded ? true : false}
            aria-controls="map-info-panel-popular-products-panel"
            role="button"
            onClick={() => {
              setIsPopularProductsPanelExpanded(true);
              changeMapType(MapType.Heat);
            }}
          >
            <House />
            <div className="info-panel-child-box">
              <p className="info-panel-child-box-description">
                See which products are popular
              </p>
              <ChevronRight />
            </div>
          </a>
        </li>
        <li>
          <a
            className="info-panel-box bg-secondary hidden sm:block"
            aria-labelledby="loan-information"
            href="javascript:void(0);"
            aria-expanded={isLoansPanelExpanded ? true : false}
            aria-controls="map-info-panel-loans-panel"
            role="button"
            onClick={() => {
              setIsLoansPanelExpanded(true);
              changeMapType(MapType.Heat);
            }}
          >
            <PiggyBank />
            <div className="info-panel-child-box">
              <p className="info-panel-child-box-description">
                See how many loans and their average size
              </p>
              <ChevronRight />
            </div>
          </a>
        </li>
        <li>
          <a
            className="info-panel-box bg-tertiary hidden sm:block"
            aria-labelledby="energy-generation"
            href="javascript:void(0);"
            aria-expanded={isSolarInfoPanelExpanded ? true : false}
            aria-controls="map-info-panel-energy-generation-panel"
            role="button"
            onClick={() => {
              setIsSolarInfoPanelExpanded(true);
              changeMapType(MapType.Heat);
            }}
          >
            <SolarPanel />
            <div className="info-panel-child-box">
              <p className="info-panel-child-box-description text-black">
                See solar capacity and uptake
              </p>
              <ChevronRight fill="black" />
            </div>
          </a>
        </li>
      </ul>
      {location && <LocationPanel onClose={() => setLocation(null)} />}

      {isPopularProductsPanelExpanded && (
        <PopularProductsPanel
          onClose={() => {
            setIsPopularProductsPanelExpanded(false);
            changeMapType(MapType.Marker);
          }}
          setHeatMapFilter={setHeatMapFilter}
          funFacts={funFacts}
          setLocation={setLocation}
          locations={locations}
        />
      )}

      {isLoansPanelExpanded && (
        <LoansPanel
          onClose={() => {
            setIsLoansPanelExpanded(false);
            changeMapType(MapType.Marker);
          }}
          setHeatMapFilter={setHeatMapFilter}
          funFacts={funFacts}
          setLocation={setLocation}
        />
      )}

      {isSolarInfoPanelExpanded && (
        <SolarInfoPanel
          onClose={() => {
            setIsSolarInfoPanelExpanded(false);
            changeMapType(MapType.Marker);
          }}
          setHeatMapFilter={setHeatMapFilter}
          funFacts={funFacts}
          setLocation={setLocation}
        />
      )}
    </>
  );
}
